/** SCR-03 / 04 / 05 / 06 — the individual, company-admin and member dashboards, and the company Team Dashboard. */
import { Link, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, Card, StatTile, Spinner, ErrorState, EmptyState, Badge } from '../../components/common/index.jsx';
import { DonutChart, BarChart } from '../../components/charts/index.jsx';
import { relativeTime } from '../../utils/formatters.js';
import * as api from '../../api/partnerPortal.api.js';
import { SUBMISSION } from './partnerShared.jsx';

const STATUS_COLORS = { draft: '#94a3b8', in_review: '#0ea5e9', information_requested: '#f59e0b', approved: '#10b981', rejected: '#f43f5e' };

function useDash() {
  return {
    summary: useQuery({ queryKey: ['partner', 'dash', 'summary'], queryFn: api.dashboardSummary }),
    stats: useQuery({ queryKey: ['partner', 'dash', 'stats'], queryFn: api.leadStatistics }),
    trend: useQuery({ queryKey: ['partner', 'dash', 'trend'], queryFn: api.monthlyTrends }),
    recent: useQuery({ queryKey: ['partner', 'dash', 'recent'], queryFn: api.recentActivities }),
  };
}

/** A labelled row of tiles — grouping is what makes the dashboard scannable. */
function Section({ title, hint, tiles, cols = 4 }) {
  return (
    <section className="mb-4">
      <div className="d-flex align-items-baseline gap-2 mb-2">
        <div className="eyebrow">{title}</div>
        {hint && <div className="t-xs text-muted">{hint}</div>}
      </div>
      <div className="row g-3">
        {tiles.map((t) => <div className={`col-6 col-xl-${12 / Math.min(4, tiles.length)}`} key={t.label}><StatTile {...t} /></div>)}
      </div>
    </section>
  );
}

function Tiles({ s, role }) {
  const mine = role === 'member' ? 'My ' : '';
  return (
    <>
      {s.information_requested > 0 && (
        <Link to="/partner/leads?status=information_requested" className="alert-x alert-warning-x d-flex align-items-center gap-2 mb-4 text-decoration-none">
          <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
          <span className="fw-600">{s.information_requested} lead{s.information_requested === 1 ? '' : 's'} need more information</span>
          <span className="ms-auto t-xs">Review <i className="bi bi-arrow-right" aria-hidden="true" /></span>
        </Link>
      )}
      <Section
        title="Leads" hint={role === 'member' ? 'Submitted by you' : undefined}
        tiles={[
          { label: `${mine}Total leads`, value: s.total_leads, icon: 'bi-magnet-fill', tone: 'brand' },
          { label: 'In review', value: s.leads_in_review, icon: 'bi-hourglass-split', tone: 'sky' },
          { label: 'Approved', value: s.leads_approved, icon: 'bi-check2-circle', tone: 'emerald' },
          { label: 'Rejected', value: s.leads_rejected, icon: 'bi-x-circle', tone: 'rose' },
        ]}
      />
      <Section
        title="Deals"
        tiles={[
          { label: `${mine}Active deals`, value: s.active_deals, icon: 'bi-briefcase-fill', tone: 'violet' },
          ...(role === 'member' ? [] : [{ label: 'Won deals', value: s.won_deals, icon: 'bi-trophy-fill', tone: 'amber' }]),
          { label: 'Need information', value: s.information_requested ?? 0, icon: 'bi-question-circle-fill', tone: 'amber' },
        ]}
      />
      {role === 'admin' && (
        <Section
          title="Team" hint={`${s.active_team_members} of ${s.team_members} members active`}
          tiles={[
            { label: 'Team members', value: s.team_members, icon: 'bi-people-fill', tone: 'sky' },
            { label: 'Submitted by team', value: s.leads_submitted_by_team, icon: 'bi-person-lines-fill', tone: 'brand' },
          ]}
        />
      )}
    </>
  );
}

function Recent({ recent, role }) {
  const r = recent.data;
  return (
    <section>
    <div className="eyebrow mb-2">Latest</div>
    <div className="row g-3">
      <div className="col-lg-4">
        <Card title={role === 'member' ? 'My recent activities' : 'Lead activity'} bodyClass="d-flex flex-column gap-2 t-sm">
          {!r?.activities.length && <div className="text-muted">No activity yet.</div>}
          {r?.activities.map((a, i) => (<div key={i}><div className="fw-600">{a.activity_title}</div><div className="t-xs text-muted">{a.partner_lead_code} · {relativeTime(a.activity_date)}</div></div>))}
        </Card>
      </div>
      <div className="col-lg-4">
        <Card title="Recent comments" bodyClass="d-flex flex-column gap-2 t-sm">
          {!r?.comments.length && <div className="text-muted">No comments yet.</div>}
          {r?.comments.map((c, i) => (<div key={i}><div><span className="fw-600">{c.author_name}</span> <span className="text-muted">on {c.partner_lead_code}</span></div><div className="t-xs">{c.text}</div><div className="t-2xs text-muted">{relativeTime(c.created_at)}</div></div>))}
        </Card>
      </div>
      <div className="col-lg-4">
        <Card title="Recent notifications" actions={<Link to="/partner/notifications" className="t-xs">All</Link>} bodyClass="d-flex flex-column gap-2 t-sm">
          {!r?.notifications.length && <div className="text-muted">Nothing yet.</div>}
          {r?.notifications.map((n) => (<div key={n.id} className={n.is_read ? '' : 'fw-650'}>{n.title}<div className="t-2xs text-muted fw-400">{relativeTime(n.created_at)}</div></div>))}
        </Card>
      </div>
    </div>
    </section>
  );
}

function Charts({ stats, trend }) {
  const dist = (stats.data?.submission_status ?? []).map((s) => ({ label: SUBMISSION[s.status]?.label ?? s.status, value: s.count, status: s.status }));
  return (
    <section className="mb-4">
    <div className="eyebrow mb-2">Overview</div>
    <div className="row g-3">
      <div className="col-lg-5">
        <Card title="Lead status distribution">
          {dist.length === 0 ? <div className="t-sm text-muted">No leads submitted yet.</div> : (
            <DonutChart data={dist} height={230} centerValue={dist.reduce((a, d) => a + d.value, 0)} centerLabel="leads" colors={dist.map((d) => STATUS_COLORS[d.status])} />
          )}
        </Card>
      </div>
      <div className="col-lg-7">
        <Card title="Monthly lead submissions" subtitle="Last 12 months">
          <BarChart data={(trend.data ?? []).map((m) => ({ ...m, month: m.month.slice(2) }))} xKey="month" height={230}
            series={[{ key: 'submitted', label: 'Submitted' }, { key: 'approved', label: 'Approved' }]} />
        </Card>
      </div>
    </div>
    </section>
  );
}

export function PartnerDashboard() {
  const me = useOutletContext();
  const d = useDash();
  if (d.summary.isLoading) return <Spinner />;
  if (d.summary.isError) return <ErrorState error={d.summary.error} onRetry={d.summary.refetch} />;
  const role = me.role;
  const title = role === 'admin' ? `${me.partner.name} — Company dashboard` : role === 'member' ? 'My dashboard' : 'Partner dashboard';
  return (
    <>
      <PageHeader title={title} subtitle={`Welcome back, ${me.user.full_name}`}>
        <Link to="/partner/leads?new=1" className="btn btn-add btn-sm-x"><i className="bi bi-plus-lg me-1" aria-hidden="true" />Add new lead</Link>
      </PageHeader>
      <Tiles s={d.summary.data} role={role} />
      <Charts stats={d.stats} trend={d.trend} />
      <Recent recent={d.recent} role={role} />
    </>
  );
}

/** SCR-05 — Company Admin only: who submitted what. */
export function TeamDashboard() {
  const q = useQuery({ queryKey: ['partner', 'dash', 'team'], queryFn: api.teamPerformance });
  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const { members, top_contributors: top } = q.data;
  return (
    <>
      <PageHeader title="Team dashboard" subtitle="Leads submitted by each team member" />
      <div className="row g-3 mb-3">
        <div className="col-lg-6">
          <Card title="Top contributors">
            {top.length === 0 ? <EmptyState icon="bi-people" title="No submissions yet" message="Leads your team submits will rank here." /> : (
              <BarChart data={top} xKey="name" height={240} series={[{ key: 'submitted', label: 'Submitted' }, { key: 'approved', label: 'Approved' }]} />
            )}
          </Card>
        </div>
        <div className="col-lg-6">
          <Card title="Member-wise contribution" bare>
            <div className="table-x-wrap"><table className="table-x is-dense">
              <thead><tr><th>Member</th><th className="text-end">Submitted</th><th className="text-end">In review</th><th className="text-end">Approved</th><th className="text-end">Rejected</th><th className="text-end">Active deals</th></tr></thead>
              <tbody>{members.map((m) => (
                <tr key={m.partner_user_id}><td><div className="fw-600">{m.full_name}</div><div className="t-xs text-muted">{m.role === 'admin' ? 'Company admin' : 'Member'} · {m.status}</div></td>
                  <td className="text-end">{m.submitted}</td><td className="text-end">{m.in_review}</td><td className="text-end">{m.approved}</td><td className="text-end">{m.rejected}</td><td className="text-end">{m.active_deals}</td></tr>
              ))}</tbody>
            </table></div>
          </Card>
        </div>
      </div>
      <Badge tone="neutral">Team activity appears here as members submit and update leads.</Badge>
    </>
  );
}
